import { useRef, useCallback } from 'react';

import * as Sentry from '@sentry/react';

import type { UnifiedEvent } from 'src/app/analytics/analyticsEvents';
import { segmentAnalyticsEvent } from 'src/app/state/actions';
import { useDispatch } from 'src/app/state/store';

/**
 * This returns an unamed `actionDispatcher` function that you
 * can manually call from your function component.
 *
 * If passing an Event Object with an associated properties array,
 * the `actionDispatcher` will console.error if the payload keys
 * don't satisfy the propteries array.
 * - The error will only log on the first call
 * - Prevents overflowing Sentry
 */
export const useLazySegmentTrack = <U extends UnifiedEvent>(event: U) => {
  const dispatch = useDispatch();

  /**
   * Use a ref to keep track of actionDispatcher calls
   * - only console.error on the intial attempt
   */
  const counter = useRef(0);
  const actionDispatcher = useCallback(
    (payload = {}) => {
      const { name, properties } = event;
      const missingProps: string[] = [];
      // Initial error message
      properties?.forEach(key => {
        if (!(key in payload)) {
          missingProps.push(key);
        }
      });

      if (counter.current === 0 && missingProps.length > 0) {
        const missingPropsStr = missingProps.map(key => `• ${key}`).join('\n');
        Sentry.captureMessage(
          `useLazySegmentTrack()\n` +
            `Missing or misspelled keys in actionDispatcher payload:\n` +
            `${name}:\n` +
            `${missingPropsStr}`
        );
      }

      counter.current++;
      dispatch(segmentAnalyticsEvent(name, { ...payload }));
    },
    [dispatch, event]
  );

  return [actionDispatcher];
};
